import { closestCorners, DndContext, DragOverlay, PointerSensor, useDroppable, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { boardCardFields, boardQuery, compareRank, type Board, type BoardCardField, type BoardPosition, type Issue, type Sprint } from "@gjwtrack/domain";
import { fieldKey, type FieldDef, type SchemaIndex } from "@gjwtrack/query-language";
import { PanelRight, Plus, Settings } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Centered } from "../App.tsx";
import { Avatar } from "../components/Badges.tsx";
import { BoardSettingsPanel } from "../components/BoardSettings.tsx";
import { BoardSwitcher } from "../components/BoardSwitcher.tsx";
import { TopBar } from "../components/TopBar.tsx";
import { client, useClient } from "../lib/client.ts";
import { chipClass, chipSlot, fieldValueText, issueKey, periodText, userName } from "../lib/format.ts";
import { Button, Dialog, DialogContent, DialogHeader, Input, InputSize, Panel } from "../ui/ring.ts";
import { SimpleSelect } from "../ui/SimpleSelect.tsx";
import { uuid } from "@gjwtrack/client";

/** Cards the board's live query keeps (server cap 1000). Bigger boards should scope by sprint or narrow the query. */
const CARD_LIMIT = 1000;
const BACKLOG = "backlog";

export function BoardPage() {
  // /agiles/:id/:sprint — sprint is "current" (by date), "all", "backlog" or a sprint id; changing it rewrites the URL, as YouTrack does.
  const { id = "", sprint = "current" } = useParams();
  const boardId = Number(id);
  const navigate = useNavigate();
  // A board created from /agiles/all lands here with its settings open, YouTrack-style.
  const [search, setSearch] = useSearchParams();
  const state = useClient((s) => s.boards[boardId]);
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const sub = `board:${boardId}:cards`;
  const view = useClient((s) => s.views[sub]);
  const display = useClient((s) => s.display);
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [settings, setSettings] = useState(search.get("settings") === "1");
  const [newSprint, setNewSprint] = useState(false);

  useEffect(() => {
    if (!Number.isInteger(boardId)) return;
    void client.openBoard(boardId);
    // Remember it: the nav's "Agile Boards" comes back here (YouTrack's /agiles → last board).
    if (client.state.me && client.state.me.settings.lastBoardId !== boardId) void client.setUserSettings({ lastBoardId: boardId });
    return () => client.closeBoard(boardId);
  }, [boardId]);
  // The cards are the board's projects narrowed by its own filter; `boardQuery` is the one place that composes them.
  const query = state?.board && schema ? boardQuery(state.board, schema) : undefined;
  useEffect(() => {
    if (query === undefined) return;
    client.openView(sub, query, CARD_LIMIT);
    return () => client.closeView(sub);
  }, [sub, query]);
  useEffect(() => {
    // Consume ?settings=1 once, so reloading the page does not reopen the panel.
    if (search.get("settings") === "1") setSearch({}, { replace: true });
  }, [search, setSearch]);

  const board = state?.board ?? null;
  const sprintsOn = board?.sprintsEnabled !== false;
  const sprints = useMemo(() => (sprintsOn ? (state?.sprints ?? []).filter((s) => !s.archived) : []), [state?.sprints, sprintsOn]);
  const now = Date.now();
  const currentSprint = sprints.find((s) => (s.startDate ?? -Infinity) <= now && now <= (s.finishDate ?? Infinity)) ?? sprints[0];
  const sprintId: number | null | "all" = sprint === "all" ? "all" : sprint === "backlog" ? null : sprint === "current" ? (currentSprint?.id ?? "all") : Number(sprint);
  const setSprintId = (v: number | null | "all"): void => void navigate(`/agiles/${boardId}/${v === "all" ? "all" : v === null ? "backlog" : v}`, { replace: true });

  if (!Number.isInteger(boardId)) return <Centered>Not a board.</Centered>;
  if (!state || !schema) return <Centered>Loading…</Centered>;
  if (state.status === "error") return <Centered>{state.error}</Centered>;
  if (!board || !view) return <Centered>Loading…</Centered>;

  const columnField = schema.fieldsById.get(board.columnFieldId);
  const swimField = board.swimlaneFieldId === null ? undefined : schema.fieldsById.get(board.swimlaneFieldId);
  const estField = board.estimationFieldId === null ? undefined : schema.fieldsById.get(board.estimationFieldId);
  const origEstField = board.originalEstimationFieldId === null ? undefined : schema.fieldsById.get(board.originalEstimationFieldId);
  if (!columnField) return <Centered>The column field no longer exists. Open the settings.</Centered>;
  const editable = me?.isAdmin || board.createdBy === me?.id;

  // Cards on the board: every issue of the live query, filtered by the selected sprint.
  const inScope = (issue: Issue): boolean => {
    if (sprintId === "all" || sprints.length === 0) return true;
    return (state.positions[issue.id]?.sprintId ?? null) === sprintId;
  };
  const all = view.ids.map((i) => display[i]).filter((i): i is Issue => i !== undefined);
  const cards = all.filter(inScope);
  const backlog = sprints.length ? all.filter((i) => (state.positions[i.id]?.sprintId ?? null) === null) : [];
  const orderOf = (i: Issue): { rank: string; issueId: number } => ({ rank: state.positions[i.id]?.rank ?? "~", issueId: i.id });
  const sorted = (xs: Issue[]): Issue[] => [...xs].sort((a, b) => compareRank(orderOf(a), orderOf(b)));

  return (
    <div className="flex h-full flex-col">
      <TopBar
        crumbs={[{ label: "Agile boards", to: "/agiles" }]}
        after={
          <span className="flex items-center gap-1.5 text-[15px]">
            <span className="text-faint">/</span>
            <BoardSwitcher current={board} />
          </span>
        }
        query=""
        onQuery={(q) => navigate(q ? `/issues?q=${encodeURIComponent(q)}` : "/issues")}
        onCreate={() => navigate("/issues")}
        right={
          <>
            {editable && <Button icon={Settings} title="Board settings" active={settings} onClick={() => setSettings((o) => !o)} />}
            {sprintsOn && <Button icon={PanelRight} title="Backlog" active={backlogOpen} onClick={() => setBacklogOpen((o) => !o)} disabled={sprints.length === 0} />}
          </>
        }
      />
      <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-2 text-sm">
        {/* Sprints off (Board Settings → General) = one flat board: no picker, no dates, no backlog. */}
        {sprintsOn && (
          <>
            <span className="text-muted">Sprint</span>
            <SimpleSelect
              value={sprintId === "all" ? "all" : sprintId === null ? BACKLOG : String(sprintId)}
              options={[{ value: "all", label: "All cards" }, ...sprints.map((s) => ({ value: String(s.id), label: s.name })), ...(sprints.length > 0 ? [{ value: BACKLOG, label: "Backlog" }] : [])]}
              onChange={(v) => setSprintId(v === "all" ? "all" : v === BACKLOG ? null : Number(v))}
            />
            {sprintId !== "all" && sprintId !== null && <SprintDates sprint={sprints.find((s) => s.id === sprintId)} />}
            {editable && (
              <Button icon={Plus} onClick={() => setNewSprint(true)}>
                New sprint
              </Button>
            )}
          </>
        )}
        <div className="flex-1" />
        <span className="text-xs text-muted">
          {cards.length} {cards.length === 1 ? "card" : "cards"}
          {view.status === "stale" || state.status === "stale" ? <span className="ml-2 text-warn">offline</span> : null}
          {view.total > view.ids.length && (
            <span className="ml-2 text-warn" title="Add #Unresolved or a sprint condition to the board query, or split the board">
              showing the first {view.ids.length} of {view.total} matching issues
            </span>
          )}
        </span>
      </div>
      {settings && editable && (
        <BoardSettingsPanel board={board} sprints={state.sprints} onClose={() => setSettings(false)} onDeleted={() => navigate("/agiles")} />
      )}
      <BoardBody
        boardId={boardId}
        board={board}
        schema={schema}
        columnField={columnField}
        swimField={swimField}
        estField={estField}
        origEstField={origEstField}
        cards={sorted(cards)}
        backlog={backlogOpen ? sorted(backlog) : null}
        positions={state.positions}
        sprintId={sprintId === "all" ? null : sprintId}
        sprintMode={sprints.length > 0 && sprintId !== "all"}
      />
      {newSprint && (
        <SprintDialog
          boardId={boardId}
          onClose={() => setNewSprint(false)}
          onSaved={(s) => {
            setNewSprint(false);
            setSprintId(s.id);
          }}
        />
      )}
    </div>
  );
}

function SprintDates({ sprint }: { sprint: Sprint | undefined }) {
  if (!sprint || (sprint.startDate === null && sprint.finishDate === null)) return null;
  const d = (ms: number | null): string => (ms === null ? "…" : new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" }));
  return (
    <span className="text-muted">
      {d(sprint.startDate)} – {d(sprint.finishDate)}
      {sprint.goal && <span className="ml-2">· {sprint.goal}</span>}
    </span>
  );
}

interface BodyProps {
  boardId: number;
  board: Board;
  schema: SchemaIndex;
  columnField: FieldDef;
  swimField: FieldDef | undefined;
  estField: FieldDef | undefined;
  /** "Original estimation field" (Board Settings → Card); set, a column header reads `current / original`. */
  origEstField: FieldDef | undefined;
  cards: Issue[];
  backlog: Issue[] | null;
  positions: Record<number, BoardPosition>;
  /** Sprint the visible cards belong to (null = backlog or no sprints). */
  sprintId: number | null;
  sprintMode: boolean;
}

/** Column id encoding for droppables: `col:<elementId>[:lane:<value>]` or `backlog`. */
const colId = (elementId: number, lane: string | null): string => (lane === null ? `col:${elementId}` : `col:${elementId}:lane:${lane}`);
const parseColId = (id: string): { elementId: number; lane: string | null } | null => {
  const m = /^col:(\d+)(?::lane:(.*))?$/.exec(id);
  return m ? { elementId: Number(m[1]), lane: m[2] ?? null } : null;
};

/** What Board Settings → Card says a card shows, resolved against the schema once for the whole board. */
interface CardConfig {
  fields: { field: FieldDef; display: BoardCardField["display"] }[];
  /** Colour scheme field; its value paints the stripe down the left of a card. */
  colorField: FieldDef | undefined;
  /** "Show colors for other custom fields": false = neutral chips (the stripe keeps its colour). */
  colors: boolean;
}

function BoardBody({ boardId, board, schema, columnField, swimField, estField, origEstField, cards, backlog, positions, sprintId, sprintMode }: BodyProps) {
  const [dragging, setDragging] = useState<number | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const colKey = fieldKey(columnField.id);
  const laneKey = swimField ? fieldKey(swimField.id) : null;
  const laneOf = (i: Issue): string => (laneKey ? String(i.fields[laneKey]?.[0] ?? "") : "");
  const columns = board.columns.map((c) => ({ ...c, element: schema.elementsById.get(c.elementId) })).filter((c) => c.element);

  // Swimlanes: values of the swimlane field present among the cards, in option order, plus "none".
  const lanes: { key: string; label: string }[] = useMemo(() => {
    if (!swimField) return [{ key: "", label: "" }];
    const present = new Set(cards.map(laneOf));
    const out: { key: string; label: string }[] = [];
    if (swimField.bundleId !== undefined) {
      for (const el of schema.elementsByBundle.get(swimField.bundleId) ?? []) if (present.has(String(el.id))) out.push({ key: String(el.id), label: el.name });
    } else {
      for (const v of [...present].filter((v) => v !== "").sort()) out.push({ key: v, label: fieldValueText(schema, swimField, /^\d+$/.test(v) ? Number(v) : v) });
    }
    if (present.has("")) out.push({ key: "", label: `No ${swimField.name.toLowerCase()}` });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, swimField, schema]);

  const card: CardConfig = useMemo(
    () => ({
      fields: boardCardFields(board, schema)
        .map((c) => ({ field: schema.fieldsById.get(c.fieldId), display: c.display }))
        .filter((c): c is { field: FieldDef; display: BoardCardField["display"] } => c.field !== undefined),
      colorField: board.colorFieldId === null ? undefined : schema.fieldsById.get(board.colorFieldId),
      colors: board.cardColors,
    }),
    [board, schema],
  );

  const cardsIn = (elementId: number, lane: string | null): Issue[] => cards.filter((i) => i.fields[colKey]?.[0] === elementId && (lane === null || laneOf(i) === lane));
  const countIn = (elementId: number): number => cards.filter((i) => i.fields[colKey]?.[0] === elementId).length;
  const estimate = (f: FieldDef | undefined, xs: Issue[]): number => (f ? xs.reduce((sum, i) => sum + Number(i.fields[fieldKey(f.id)]?.[0] ?? 0), 0) : 0);
  const estText = (f: FieldDef | undefined, n: number): string => (f?.kind === "period" ? periodText(n) : String(n));
  /** The column's estimate: one number, or `current / original` once an original estimation field is set. */
  const estSummary = (xs: Issue[]): string => {
    const cur = estText(estField, estimate(estField, xs));
    return origEstField ? `${cur} / ${estText(origEstField, estimate(origEstField, xs))}` : cur;
  };

  const onDragStart = (e: DragStartEvent): void => {
    const id = Number(e.active.id);
    setDragging(id);
    client.freeze(id);
  };
  const onDragEnd = (e: DragEndEvent): void => {
    const id = Number(e.active.id);
    setDragging(null);
    try {
      const over = e.over;
      if (!over) return;
      const overId = String(over.id);
      // Dropped on a card → that card's column; on a column → the column itself.
      const overCard = /^\d+$/.test(overId) ? Number(overId) : null;
      const target = overCard !== null ? containerOf(overCard) : overId === BACKLOG ? { backlog: true as const } : parseColId(overId);
      if (!target) return;
      const issue = cards.find((i) => i.id === id) ?? backlog?.find((i) => i.id === id);
      if (!issue) return;
      if ("backlog" in target) {
        if (positions[id]?.sprintId !== null) void client.setSprint(boardId, [id], null);
        return;
      }
      const list = cardsIn(target.elementId, target.lane).filter((i) => i.id !== id);
      let index = overCard !== null ? list.findIndex((i) => i.id === overCard) : list.length;
      if (index < 0) index = list.length;
      // Dropping onto a card from above places before it, from below places after it.
      if (overCard !== null && e.delta.y > 0 && e.active.rect.current.translated && over.rect && e.active.rect.current.translated.top > over.rect.top + over.rect.height / 2) index++;
      const after = index > 0 ? list[index - 1]!.id : null;
      const before = index < list.length ? list[index]!.id : null;
      const patch: Record<string, ReadonlyArray<number | string>> = {};
      if (issue.fields[colKey]?.[0] !== target.elementId) patch[colKey] = [target.elementId];
      if (target.lane !== null && laneKey && laneOf(issue) !== target.lane && swimField) patch[laneKey] = target.lane === "" ? [] : [/^\d+$/.test(target.lane) ? Number(target.lane) : target.lane];
      if (Object.keys(patch).length) client.updateIssue(id, { fields: patch });
      const fromBacklog = sprintMode && (positions[id]?.sprintId ?? null) !== sprintId;
      void client.moveCard(boardId, id, { afterIssueId: after, beforeIssueId: after === null ? before : null, ...(fromBacklog ? { sprintId } : {}) });
    } finally {
      client.unfreeze(id);
    }
  };
  const containerOf = (cardId: number): { elementId: number; lane: string | null } | { backlog: true } | null => {
    const c = cards.find((i) => i.id === cardId);
    if (c) return { elementId: Number(c.fields[colKey]?.[0]), lane: swimField ? laneOf(c) : null };
    if (backlog?.some((i) => i.id === cardId)) return { backlog: true };
    return null;
  };

  const draggingIssue = dragging === null ? undefined : (cards.find((i) => i.id === dragging) ?? backlog?.find((i) => i.id === dragging));
  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => dragging !== null && (client.unfreeze(dragging), setDragging(null))}>
      <div className="flex min-h-0 flex-1">
        <div className="min-h-0 flex-1 overflow-auto">
          <div className="grid min-w-max gap-3 p-4" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(15rem, 20rem))` }}>
            {columns.map((c) => {
              const n = countIn(c.elementId);
              const over = c.wip !== null && n > c.wip;
              return (
                <div key={c.elementId} className="flex items-center gap-2 px-1 text-[13px]">
                  <span className={`badge ${c.element!.colorId ? `chip-${((c.element!.colorId - 1) % 8) + 1}` : ""}`}>{c.element!.name[0]}</span>
                  <span className="font-semibold">{c.element!.name}</span>
                  <span className={`tabular-nums ${over ? "font-semibold text-danger" : "text-muted"}`}>
                    {n}
                    {c.wip !== null && ` / ${c.wip}`}
                  </span>
                  {(estField || origEstField) && <span className="ml-auto text-muted tabular-nums">{estSummary(cardsIn(c.elementId, null))}</span>}
                </div>
              );
            })}
            {lanes.map((lane) => (
              <LaneRow key={lane.key} label={swimField ? lane.label : null} columnsCount={columns.length}>
                {columns.map((c) => {
                  const list = cardsIn(c.elementId, swimField ? lane.key : null);
                  return (
                    <Column key={c.elementId} id={colId(c.elementId, swimField ? lane.key : null)} over={c.wip !== null && countIn(c.elementId) > c.wip}>
                      <SortableContext items={list.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                        {list.map((i) => (
                          <Card key={i.id} issue={i} schema={schema} card={card} />
                        ))}
                      </SortableContext>
                    </Column>
                  );
                })}
              </LaneRow>
            ))}
          </div>
        </div>
        {backlog && (
          <aside className="flex w-72 shrink-0 flex-col border-l border-line bg-surface">
            <div className="flex items-center gap-2 border-b border-line px-3 py-2 text-[13px] font-semibold">
              Backlog <span className="text-muted tabular-nums">{backlog.length}</span>
            </div>
            <Column id={BACKLOG} over={false} className="min-h-0 flex-1 overflow-y-auto p-2">
              <SortableContext items={backlog.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                {backlog.map((i) => (
                  <Card key={i.id} issue={i} schema={schema} card={card} />
                ))}
              </SortableContext>
              {backlog.length === 0 && <div className="p-2 text-faint">Nothing in the backlog. Drag cards here to take them out of the sprint.</div>}
            </Column>
          </aside>
        )}
      </div>
      <DragOverlay>{draggingIssue ? <CardBody issue={draggingIssue} schema={schema} card={card} lifted /> : null}</DragOverlay>
    </DndContext>
  );
}

function LaneRow({ label, columnsCount, children }: { label: string | null; columnsCount: number; children: React.ReactNode }) {
  return (
    <>
      {label !== null && (
        <div className="mt-2 border-b border-line pb-1 text-[13px] font-medium text-muted" style={{ gridColumn: `1 / span ${columnsCount}` }}>
          {label}
        </div>
      )}
      {children}
    </>
  );
}

function Column({ id, over, className = "", children }: { id: string; over: boolean; className?: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`flex min-h-24 flex-col gap-2 rounded-md p-2 ${over ? "bg-danger-soft/40" : "bg-sunken/60"} ${isOver ? "ring-2 ring-accent/50" : ""} ${className}`}>
      {children}
    </div>
  );
}

function Card({ issue, schema, card }: { issue: Issue; schema: SchemaIndex; card: CardConfig }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: issue.id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }} {...attributes} {...listeners} className={isDragging ? "opacity-30" : ""}>
      <CardBody issue={issue} schema={schema} card={card} />
    </div>
  );
}

/**
 * A card shows what Board Settings → Card says it shows, in that order: people
 * along the top (an avatar, plus the name when the field is set to Full),
 * everything else as chips under the summary. Tags always follow the chips.
 */
function CardBody({ issue, schema, card, lifted }: { issue: Issue; schema: SchemaIndex; card: CardConfig; lifted?: boolean }) {
  const key = issueKey(schema, issue);
  const values = card.fields.flatMap(({ field, display }) => (issue.fields[fieldKey(field.id)] ?? []).map((v) => ({ key: `${field.id}:${v}`, field, display, v })));
  const people = values.filter((x): x is typeof x & { v: number } => x.field.kind === "user" && typeof x.v === "number");
  const chips = values.filter((x) => x.field.kind !== "user");
  // Colour scheme: the stripe is its own element so the chip-N class cannot leak its tokens into the rest of the card.
  const stripe = card.colorField ? chipSlot(schema, issue.fields[fieldKey(card.colorField.id)]?.[0] ?? "") : 0;
  return (
    <div className={`relative rounded-md border border-line bg-surface p-2.5 ${lifted ? "shadow-lg" : "hover:border-accent/60"}`} data-issue-id={issue.id} data-issue-key={key}>
      {stripe > 0 && <span className={`card-stripe chip-${stripe}`} data-color-slot={stripe} />}
      <div className="mb-1 flex items-center gap-2">
        <Link to={`/issue/${key}`} className={`font-mono text-xs ${issue.resolvedAt !== null ? "text-faint line-through" : "text-muted"} hover:underline`} onPointerDown={(e) => e.stopPropagation()}>
          {key}
        </Link>
        <span className="flex-1" />
        {people.map((x) => (
          <span key={x.key} className="flex min-w-0 items-center gap-1 text-xs text-muted">
            <Avatar schema={schema} userId={x.v} size={20} />
            {x.display === "full" && <span className="max-w-24 truncate">{userName(schema, x.v)}</span>}
          </span>
        ))}
      </div>
      <div className={`text-[13.5px] leading-snug ${issue.resolvedAt !== null ? "text-muted line-through" : ""}`}>{issue.summary}</div>
      {(chips.length > 0 || issue.tags.length > 0) && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {chips.map((x) => {
            const text = fieldValueText(schema, x.field, x.v);
            return (
              <span key={x.key} className={card.colors ? chipClass(schema, x.field, x.v) : "chip"} title={`${x.field.name}: ${text}`}>
                {x.display === "letter" ? ([...text][0] ?? "") : text}
              </span>
            );
          })}
          {issue.tags.map((t) => (
            <span key={t} className="chip chip-6">{schema.tagsById.get(t)?.name ?? `#${t}`}</span>
          ))}
        </div>
      )}
      {people.map((x) => (
        <span key={`sr-${x.key}`} className="sr-only">
          {userName(schema, x.v)}
        </span>
      ))}
    </div>
  );
}

function SprintDialog({ boardId, onClose, onSaved }: { boardId: number; onClose: () => void; onSaved: (s: Sprint) => void }) {
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [start, setStart] = useState("");
  const [finish, setFinish] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      const s = await client.http.command<Sprint>(
        "createSprint",
        { boardId, name: name.trim(), goal: goal.trim() || null, startDate: start ? Date.parse(`${start}T00:00:00Z`) : null, finishDate: finish ? Date.parse(`${finish}T00:00:00Z`) : null },
        uuid(),
      );
      onSaved(s);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <Dialog show label="New sprint" onCloseAttempt={onClose} trapFocus autoFocusFirst={false} contentClassName="!w-[26rem]">
      <DialogHeader>New sprint</DialogHeader>
      <DialogContent>
        <form id="new-sprint" onSubmit={(e) => void submit(e)} className="flex flex-col gap-2" aria-label="New sprint">
          <Input label="Name" size={InputSize.FULL} value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
          <Input label="Goal (optional)" size={InputSize.FULL} value={goal} onChange={(e) => setGoal(e.target.value)} />
          <div className="flex gap-2">
            <Input label="Start" size={InputSize.FULL} type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            <Input label="Finish" size={InputSize.FULL} type="date" value={finish} onChange={(e) => setFinish(e.target.value)} />
          </div>
          {error && <div className="text-danger">{error}</div>}
        </form>
      </DialogContent>
      <Panel>
        <Button primary type="submit" form="new-sprint" disabled={!name.trim()}>Create</Button>
        <Button onClick={onClose}>Cancel</Button>
      </Panel>
    </Dialog>
  );
}
